/**
 * Border Design Tokens
 *
 * Standard border radius values for consistent UI element shapes.
 * These tokens ensure visual consistency across buttons, cards, inputs, and containers.
 *
 * Usage:
 * - sx={{ borderRadius: tokens.borderRadius.md }}
 * - theme.tokens.borderRadius.lg
 */

export const borderRadius = {
  /** 0px - No border radius (square corners) */
  none: '0px',

  /** 4px - Small radius for compact elements (chips, badges, small buttons) */
  sm: '4px',

  /** 8px - Medium radius, default for most elements (buttons, inputs, small cards) */
  md: '8px',

  /** 12px - Large radius for cards and prominent containers */
  lg: '12px',

  /** 16px - Extra large radius for large containers and modals */
  xl: '16px',

  /** 20px - 2X large radius for special large elements */
  '2xl': '20px',

  /** 9999px - Full/circular radius for pills, avatar shapes, and circular buttons */
  full: '9999px',
} as const;

/**
 * Border width tokens for consistent stroke weights
 */
export const borderWidth = {
  /** 0px - No border */
  none: '0px',

  /** 1px - Default thin border */
  thin: '1px',

  /** 2px - Medium border for emphasis */
  medium: '2px',

  /** 3px - Thick border for strong emphasis */
  thick: '3px',

  /** 4px - Extra thick border for very strong emphasis */
  xthick: '4px',
} as const;

/**
 * Semantic border tokens for specific use cases
 */
export const semanticBorders = {
  /** Default button border radius */
  button: borderRadius.md,

  /** Input field border radius */
  input: borderRadius.md,

  /** Card border radius */
  card: borderRadius.lg,

  /** Modal/Dialog border radius */
  modal: borderRadius.xl,

  /** Chip/Badge border radius */
  chip: borderRadius.full,

  /** Avatar border radius */
  avatar: borderRadius.full,

  /** Default border width for dividers */
  divider: borderWidth.thin,

  /** Default border width for inputs */
  inputBorder: borderWidth.thin,

  /** Focus border width */
  focusBorder: borderWidth.medium,
} as const;

export type BorderRadiusKey = keyof typeof borderRadius;
export type BorderWidthKey = keyof typeof borderWidth;
export type SemanticBordersKey = keyof typeof semanticBorders;
